import { createContext, useContext } from 'react';
import { useLocation } from 'wouter';

// Non-English languages get a URL path prefix (e.g. /es/bybit) so crawlers can index
// and rank each localized page independently. English stays unprefixed at the root
// paths (/, /bybit, ...) to preserve every existing URL, backlink, and bookmark.
// Reduced from 15 locales to en + id on 2026-09-29; the removed ones 301 to English
// (REMOVED_LOCALES in scripts/redirects.mjs). scripts/check-locales.mjs keeps every
// locale list in the repo in agreement with this one.
export const NON_DEFAULT_LANGS = ['id'] as const;
export const ALL_LANG_CODES = ['en', ...NON_DEFAULT_LANGS] as const;
export type LangCode = (typeof ALL_LANG_CODES)[number];

/** URL prefix for a language: '' for English, '/es' etc. for the rest. */
export function langUrlPrefix(lang: string): string {
  return (NON_DEFAULT_LANGS as readonly string[]).includes(lang) ? `/${lang}` : '';
}

/** Builds a full path for a lang-agnostic path (always starting with '/') in the given language.
 *
 * Always returns trailing-slash form — the static host 301s every non-slash URL to its
 * slash variant, so internal <a href> links must point straight at the final (200) form
 * or Googlebot sees a redirect chain and reports a "redirect error" in Search Console.
 */
export function langHref(lang: string, path: string): string {
  const prefix = langUrlPrefix(lang);
  // Normalise to trailing-slash form so links never trigger the host's non-slash→slash 301.
  const withSlash = path.endsWith('/') ? path : `${path}/`;
  if (!prefix) return withSlash;
  // '/id' + '/' → '/id/', '/id' + '/guides/' → '/id/guides/'
  return withSlash === '/' ? `${prefix}/` : `${prefix}${withSlash}`;
}

/**
 * Absolute URL (canonical/hreflang/og:url/JSON-LD) for an already-localized href.
 * Always trailing-slash form: the static host 301s every non-slash URL to its
 * slash variant, so absolute URLs must point straight at the final (200) form —
 * otherwise Google sees canonical→301→page loops and refuses to index.
 */
export function absoluteUrl(relativeHref: string): string {
  const withSlash = relativeHref.endsWith('/') ? relativeHref : `${relativeHref}/`;
  return `https://cryptperks.com${withSlash}`;
}

/** Absolute canonical URL for a lang-agnostic path in the given language. */
export function absoluteLangUrl(lang: string, path: string): string {
  return absoluteUrl(langHref(lang, path));
}

/** hreflang alternates (all supported languages + x-default) for a lang-agnostic path. */
/**
 * @param langs Restrict the alternates to these languages. Pass this when a page
 *   does not genuinely exist in every language -- declaring an hreflang for a URL
 *   that serves untranslated English is a false signal, and search engines that
 *   catch it discount the hreflang cluster as a whole.
 */
/**
 * Languages written right-to-left. Empty since Arabic and Urdu were removed on
 * 2026-09-29, but the RTL code paths (isRtlLang, the dir attributes on every
 * page) are kept on purpose: ripping them out would touch a dozen pages for no
 * gain, and a future RTL locale then only needs adding here (and in
 * scripts/routes.mjs). The check used to live as `lang === 'ar'` copied into six
 * separate pages, so Urdu pages rendered left-to-right -- keep it centralised.
 */
export const RTL_LANGS = new Set<string>();

/** Whether a language code (with or without region suffix) is right-to-left. */
export function isRtlLang(lang: string): boolean {
  return RTL_LANGS.has(lang.split('-')[0]);
}

export function buildHreflangAlternates(
  path: string,
  langs: readonly string[] = ALL_LANG_CODES,
): { hreflang: string; href: string }[] {
  const alternates: { hreflang: string; href: string }[] = langs.map((code) => ({
    hreflang: code,
    href: absoluteLangUrl(code, path),
  }));
  alternates.push({ hreflang: 'x-default', href: absoluteLangUrl('en', path) });
  return alternates;
}

// Open Graph locale codes (og:locale expects language_TERRITORY, not a bare ISO code).
export const OG_LOCALE_MAP: Record<string, string> = {
  en: 'en_US',
  id: 'id_ID',
};

export function ogLocaleFor(lang: string): string {
  return OG_LOCALE_MAP[lang] ?? OG_LOCALE_MAP.en;
}

/** og:locale:alternate values for every other supported language. */
export function ogLocaleAlternates(lang: string): string[] {
  return ALL_LANG_CODES.filter((code) => code !== lang).map((code) => OG_LOCALE_MAP[code]);
}

export const LangContext = createContext<string>('en');

export function useLang(): string {
  return useContext(LangContext);
}

/** Returns a function that builds a same-language href for a lang-agnostic path. */
export function useLangHref(): (path: string) => string {
  const lang = useContext(LangContext);
  return (path: string) => langHref(lang, path);
}

/** Strips the current language's URL prefix off the current wouter location. */
export function useLangAgnosticPath(): string {
  const [location] = useLocation();
  const lang = useContext(LangContext);
  const prefix = langUrlPrefix(lang);
  if (!prefix) return location || '/';
  if (location === prefix) return '/';
  if (location.startsWith(`${prefix}/`)) return location.slice(prefix.length) || '/';
  return location || '/';
}
